import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Youtube } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { useSettings } from "../context/SettingsContext";
import { NerdSticker } from "../components/NerdBadge";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.6c-1.3.1-2.5-.3-3.7-1v6.9c0 4.3-3.7 7-7.1 5.8-2.9-1-4.1-4.4-2.6-7 .9-1.6 2.6-2.5 4.4-2.4v2.7c-.4-.1-.9-.1-1.3 0-1.5.4-2 1.9-1.3 3.1.7 1.2 2.6 1.2 3.2-.3.1-.4.2-.8.2-1.2V3h4.1z" />
  </svg>
);

const LEVELS = [
  {
    difficulty: "5th Grader",
    color: "#2C7B76",
    quote: "Explain it like I'm in 5th grade.",
    body: "The absolute basics. If you've never touched AI, start here. No jargon, nothing to install, nothing to buy — just one simple thing you can do today. If you can text, you can do a 5th Grader video.",
    see: "Your first AI chat, asking for anything in plain English, everyday wins like emails and planning.",
  },
  {
    difficulty: "Chess Club",
    color: "#C08A2E",
    quote: "Now we're building.",
    body: "A step up. You've used AI a little and you're ready to make something — a simple app, a tracker, a tool that's yours. Still plain English, still no code, just a bit more moving parts.",
    see: "Building your own little apps, setting AI up for your life, connecting a tool or two.",
  },
  {
    difficulty: "Full Pocket Protector",
    color: "#C0472B",
    quote: "Top tier. Ring the bell.",
    body: "The deep end. This is where we geek out — the powerful, behind-the-scenes stuff. You don't need to start here, and you never have to come here. But when you're ready, this is where AI stops being a toy and starts running things for you.",
    see: "Automations, agents, the systems that work while you sleep.",
  },
];

export default function NerdScale() {
  const { settings } = useSettings();
  useSeo({
    title: "The Nerd Scale — Practical AI for Everyday Life",
    description: "Every video is rated on the Nerd Scale so you always know how technical it is before you start: 5th Grader, Chess Club, or Full Pocket Protector.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="nerdscale-hero">
        <div className="plexus-edge-corner absolute inset-0 opacity-30" />
        <div className="container-brand relative py-20 md:py-24">
          <p className="eyebrow !text-ochre-bright">The Nerd Scale</p>
          <h1 className="mt-4 max-w-3xl text-5xl leading-[1] text-bone sm:text-6xl">
            Always know the level <span className="italic text-ochre-bright">before we begin.</span>
          </h1>
          <p className="mt-6 max-w-measure text-lg text-on-navy-body">
            Every video I make starts the same way: I rate the topic on my Nerd Scale. It's the carnival
            strength-tester you've seen at fairs — you swing the hammer, the puck climbs, and a light tells you
            how far it got. Mine measures one thing: <strong className="text-bone">how technical today's topic is.</strong>
          </p>
          <p className="mt-4 max-w-measure text-lg text-on-navy-body">
            No surprises, no getting in over your head. You always know the level before we begin — so if you're
            brand new, you can stick to the easy stuff, and if you're ready to level up, you'll know exactly when.
          </p>
        </div>
      </section>

      {/* THREE LEVELS */}
      <section className="sheet grain">
        <div className="container-brand py-20 md:py-24">
          <div className="mb-14">
            <p className="eyebrow">The three levels</p>
            <h2 className="mt-3 text-4xl leading-[1.05] md:text-5xl">Pick your level. <span className="italic text-ochre">Climb at your pace.</span></h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {LEVELS.map((lvl, i) => (
              <motion.div
                key={lvl.difficulty}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
                className="brand-card relative flex flex-col overflow-hidden p-8"
                data-testid={`level-${lvl.difficulty.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className="absolute inset-x-0 top-0 h-1.5" style={{ background: lvl.color }} />
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="h-3 w-3 rounded-full" style={{ background: lvl.color }} />
                    <h3 className="text-[26px] leading-none text-navy">{lvl.difficulty}</h3>
                  </div>
                  <NerdSticker difficulty={lvl.difficulty} width={64} rotate={4} />
                </div>
                <p className="mt-4 font-serif text-xl italic" style={{ color: lvl.color }}>"{lvl.quote}"</p>
                <p className="mt-4 text-[15px] text-text-body">{lvl.body}</p>
                <div className="mt-6 border-t border-navy/10 pt-4">
                  <p className="font-sans text-[11px] font-bold uppercase tracking-[0.16em] text-text-label">You'll see this for</p>
                  <p className="mt-2 text-[14px] text-text-muted">{lvl.see}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO USE + WHY */}
      <section className="bg-warm grain border-y border-navy/10">
        <div className="container-brand grid gap-12 py-20 md:grid-cols-2">
          <div>
            <p className="eyebrow">How to use it</p>
            <h2 className="mt-3 text-3xl leading-tight md:text-4xl">Climb at your pace, not mine.</h2>
            <p className="mt-5 text-text-body">
              Pick your level and stay there as long as you like — there's no rush. Most people start at
              5th Grader, get a couple of quick wins, and drift up to Chess Club when they're curious. You climb
              the scale at your pace, not mine.
            </p>
          </div>
          <div>
            <p className="eyebrow">Why it exists</p>
            <h2 className="mt-3 text-3xl leading-tight md:text-4xl">So you never feel dumb, never feel lost.</h2>
            <p className="mt-5 text-text-body">
              AI is intimidating mostly because nobody tells you how hard something's going to be before you
              start. The Nerd Scale fixes that. It's a promise: I'll always tell you the level up front, in plain
              English. That's the whole channel in one little meter —
              <span className="italic text-ochre"> I stay on top of AI so you don't have to.</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="nerdscale-cta">
        <div className="container-brand relative py-20 text-center md:py-24">
          <h2 className="mx-auto max-w-2xl text-4xl leading-[1.1] text-bone md:text-5xl">
            Every video, <span className="italic text-ochre-bright">rated before we begin.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-on-navy-body">Grab a free setup and work your way up the scale at your own pace.</p>
          <div className="on-navy mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/setups" className="btn btn-primary" data-testid="nerdscale-start">
              Browse the setups <ArrowRight size={16} />
            </Link>
            <Link to="/my-ai" className="btn btn-secondary" data-testid="nerdscale-setups">
              Meet Mochi
            </Link>
            <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="nerdscale-youtube">
              <Youtube size={16} /> YouTube
            </a>
            <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="nerdscale-tiktok">
              <TikTokIcon size={15} /> TikTok
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
